import { ButtonHTMLAttributes, forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '@utils'

export const iconButtonVariants = cva(
  'inline-flex p-0 items-center justify-center rounded-xl font-semibold transition-colors focus:outline-none disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-cw-strong-grey text-white hover:bg-cw-strong-grey/[.6] disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey data-[state=open]:bg-white data-[state=open]:text-cw-midnight-purple',
        primary:
          'bg-cw-electric-purple text-white hover:bg-cw-electric-purple/80 disabled:bg-cw-lighter-grey-stroke disabled:text-cw-light-grey data-[state=open]:bg-cw-electric-purple/80',
        secondary:
          '!text-cw-yellow border border-cw-yellow hover:bg-cw-yellow-hov/[.12] disabled:text-cw-light-grey-disable disabled:border-cw-lighter-grey-stroke data-[state=open]:bg-cw-yellow-hov/[.12]',
        tertiary:
          '!text-cw-yellow hover:text-yellow-hov disabled:text-cw-light-grey-disable disabled:border-cw-light-grey-disable',
        grey: '!text-cw-light-grey-2 border border:cw-lighter-grey-stroke hover:bg-cw-light-grey-2/[.1] disabled:text-cw-light-grey-2 disabled:border-cw-light-grey-2 data-[state=open]:bg-cw-light-grey-2/[.1]',
        unstyled: '',
      },
      size: {
        md: 'h-10 w-10 p-2 text-body2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof iconButtonVariants> & {
    asChild?: boolean
  }

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        className={cn(iconButtonVariants({ className, variant, size }))}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
IconButton.displayName = 'IconButton'
